import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import { ProgressSegmented } from '@/components/ui/ProgressSegmented';
import type { ProjectView } from '@/types/current-project';
import { projectProgress, projectStatus } from '@/lib/project-presentation';

export function ProjectsListView({
  projects,
  isClient,
  onProjectClick,
}: {
  projects: ProjectView[];
  isClient: boolean;
  onProjectClick: (id: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {projects.map((project) => {
        const progress = projectProgress(project);
        const reviewScope =
          isClient &&
          !project.cancelled &&
          !project.activeDisputeId &&
          project.proposals.some((p) => p.status === 'PendingApproval');
        return (
          <article
            key={project.projectId}
            onClick={(event) => {
              if (!(event.target as Element).closest('button'))
                onProjectClick(project.projectId);
            }}
            className="flex cursor-pointer flex-col gap-[--spacing-8] rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-2] p-[--spacing-10] text-left shadow-elevation-sm transition-colors hover:border-[--state-brand-active]"
          >
            <button
              type="button"
              aria-label={'Open project: ' + project.title}
              aria-describedby={project.projectId + '-dashboard-summary'}
              className="flex flex-1 flex-col gap-4 text-left"
              onClick={() => onProjectClick(project.projectId)}
            >
              <span
                id={project.projectId + '-dashboard-summary'}
                className="sr-only"
              >
                {projectStatus(project)}.{' '}
                {progress === null
                  ? 'Execution has not started.'
                  : progress + '% of execution milestones accepted.'}
              </span>
              <h3 className="line-clamp-2 flex-1 text-2xl font-semibold leading-[38px]">
                {project.title}
              </h3>
              <div className="w-full space-y-2">
                <ProgressSegmented value={progress ?? 0} showLabel={false} />
                <div className="flex justify-end">
                  <span className="font-inter text-base font-medium text-[--text-dark-secondary]">
                    {progress ?? 0}%
                  </span>
                </div>
              </div>
            </button>
            <div className="flex items-center justify-end">
              <Button
                variant="primary"
                size="md"
                className="w-fit"
                onClick={() =>
                  reviewScope
                    ? navigate(
                        '/projects/' + project.projectId + '/review-scope'
                      )
                    : onProjectClick(project.projectId)
                }
              >
                Review Milestones &amp; Cost{' '}
                <i aria-hidden="true" className="ri-arrow-right-line" />
              </Button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
